// Per-context rule evaluation for the unified renderer. The order scope and
// every widget instance each get their own evaluation context (ancestor values
// layered underneath), so visibility/prefill rules work *inside* repeatable
// widgets and `sum` calcs aggregate across instances — the two things the flat
// legacy engine could not do. The primitives (evalWhen/applyPrefill/…) are the
// same functions the Logic page has always used.
import type { FieldDefinition, FormCard, FormConfig, FormRule } from '../shared/types';
import { flattenWidgetTree } from '../shared/types';
import { fieldVisible, showWhenPasses } from './config';
import {
  CompositeKinds, applyPrefill, cardTarget, computeCalcs, containerVisible, groupHolds, isContainerTarget,
  ruleHiddenKeys, ruleLockedKeys, ruleOptionalKeys, ruleRequiredKeys, ruleShowTargetKeys,
  ruleTargetKeys, ruleUnlockedKeys, sectionTarget, widgetTarget,
} from './rules';
import { chainOf } from './cards';
import { NO_SCORE, ScoreTotals, addTotals, isScored, scoreAnswer } from './scoring';
import { FormState, PublishedDef, WidgetInstance, instanceCounts, nodesByScope, scopeCounts, valueKey, widgetForSection } from './model';

/** Evaluation result for one context (the order scope or a single instance). */
export interface CtxEval {
  /** Effective values: ancestors + own answers with prefill mirrored on top. */
  values: Record<string, unknown>;
  hidden: Set<string>;
  /** target key -> source key for LOCKED prefill mirrors (render disabled). */
  locked: Record<string, string>;
  /** target key -> source key for EDITABLE prefills (note only). */
  prefilled: Record<string, string>;
  /**
   * Targets a prefill wrote a FIXED value into. They are in `locked` /
   * `prefilled` too, keyed to themselves for want of a source field — this set
   * is what stops a note claiming they were mirrored from one.
   */
  fixed: Set<string>;
  /**
   * `${sectionId}:${cardId}` hidden IN THIS CONTEXT. A card inside a
   * repeatable widget is answered once per instance, so it hides per instance
   * too — the card can be gone on Location 2 and present on Location 1.
   */
  hiddenCards: Set<string>;
  /**
   * Repeatable containers hidden IN THIS CONTEXT ('wnode:…' targets and the
   * builder's Hidden marker). A container nested in a widget is resolved per
   * instance like a card is: this location can ask for its devices while the
   * next one does not.
   */
  hiddenScopes: Set<string>;
  /**
   * Field keys a firing `require` / `optional` action reaches IN THIS CONTEXT,
   * containers already expanded to the fields inside them. fieldRequired reads
   * both; `optional` wins, and anything in `hidden` wins over both.
   */
  required: Set<string>;
  optional: Set<string>;
  /**
   * Field keys a firing `lock` action reaches IN THIS CONTEXT, containers
   * already expanded. These render disabled keeping the answer they have —
   * unlike `locked` above, which is a prefill mirror and also WRITES a value.
   */
  lockedByRule: Set<string>;
  /** …and the keys a firing `unlock` hands back, which beat both a `lock` and Build */
  unlockedByRule: Set<string>;
}

export interface FormEval {
  order: CtxEval;
  /** instance.id -> its context (populated for every instance in the state). */
  byInstance: Map<string, CtxEval>;
  /**
   * instance.id -> its automatic descendant counts, keyed by each node's
   * countKey, and its share of every conditional count nested inside it,
   * keyed by that count's key.
   */
  instanceCounts: Map<string, Record<string, number>>;
  calcs: Record<string, string | number>;
  counts: Record<string, number>;
  /** conditional count key -> the form-wide total (see WidgetCount) */
  conditionalCounts: Record<string, number>;
  ruleTargeted: Set<string>;
  /** keys targeted by SHOW actions — builder-hidden fields in here are rule-revealed */
  showTargeted: Set<string>;
  /** section ids hidden by the current answers ('sec:…' rule targets) */
  hiddenSections: Set<string>;
  /** `${sectionId}:${cardId}` hidden in the ORDER context ('card:…' targets) */
  hiddenCards: Set<string>;
}

/** The containers a context hides: rule targets, plus the builder's Hidden marker. */
function hiddenContainersOf(
  hidden: Set<string>, showTargeted: Set<string>,
  defaults: { cards: { section: string; id: string }[]; scopes: string[] }
): { cards: Set<string>; scopes: Set<string> } {
  const cards = new Set<string>();
  const scopes = new Set<string>();
  for (const key of hidden) {
    if (key.startsWith('card:')) cards.add(key.slice(5));
    else if (key.startsWith('wnode:')) scopes.add(key.slice(6));
  }
  for (const c of defaults.cards) {
    if (!containerVisible(cardTarget(c.section, c.id), true, hidden, showTargeted)) cards.add(`${c.section}:${c.id}`);
  }
  for (const scope of defaults.scopes) {
    if (!containerVisible(widgetTarget(scope), true, hidden, showTargeted)) scopes.add(scope);
  }
  return { cards, scopes };
}

const num = (v: unknown) => { const n = parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : 0; };

/** Add one set of counts into another, key by key. */
const tally = (into: Record<string, number>, more: Record<string, number>) => {
  for (const [key, n] of Object.entries(more)) into[key] = (into[key] || 0) + n;
};

export function evaluateForm(def: PublishedDef, state: FormState, cfg: FormConfig): FormEval {
  const rules = def.rules || [];
  const counts = scopeCounts(state, def);
  const ruleTargeted = ruleTargetKeys(rules);
  const showTargeted = ruleShowTargetKeys(rules);

  // The containers the builder marked hidden. Each waits for a show rule the
  // way a hidden field does, so they are resolved alongside the rule targets
  // rather than anywhere else.
  const sections = def.layout?.sections || [];
  const hiddenDefaults = {
    cards: sections.flatMap((s) => (s.cards || []).filter((c) => c.hidden).map((c) => ({ section: s.id, id: c.id }))),
    scopes: (def.widgets || [])
      .flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : []))
      .filter((n) => n.hidden)
      .map((n) => n.scope),
  };

  // Keys per scope, to know which effective values "belong" to an instance.
  const keysByScope: Record<string, string[]> = {};
  for (const f of def.fields) (keysByScope[f.scope] ||= []).push(f.key);

  // How each composite field re-derives its combined value when a prefill
  // writes one of its sub-paths ('shipAddress.company' and friends).
  const kinds: CompositeKinds = {};
  for (const f of def.fields) {
    if (f.input_type === 'name') kinds[f.key] = 'name';
    else if (f.input_type === 'address') kinds[f.key] = f.ui_meta?.address?.kind === 'shipping' ? 'shipping' : 'address';
  }

  // ---- order context ----
  /**
   * The order's answers, laid under this form's own for every rule that runs.
   *
   * One insertion point covers the whole form: the instance walk seeds each
   * root instance from `order.values` and each nested one from its parent's,
   * so `parent.*` resolves inside a repeatable widget exactly as it does at
   * the top — a location's devices can be revealed by what the partner
   * ordered.
   *
   * Spread LAST, so a form with a question literally keyed `parent` is
   * shadowed by the scope rather than silently breaking every rule that reads
   * it. That matches the server, where `parent` is the same reserved name for
   * the same reason (services/delivery/shared.ts:83).
   *
   * Nothing downstream treats this as an answer: `buildSubmissionData` walks
   * `def.fields`, so a key no field declares is never submitted, and the
   * renderer only ever reads values for fields it is drawing.
   */
  const ambient = def.parent ? { ...state.order, parent: def.parent } : state.order;
  const orderPre = applyPrefill(rules, ambient, state.touched, kinds);
  const orderHidden = ruleHiddenKeys(rules, orderPre.values, cfg);
  const orderContainers = hiddenContainersOf(orderHidden, showTargeted, hiddenDefaults);
  const order: CtxEval = {
    values: orderPre.values,
    hidden: orderHidden,
    locked: orderPre.locked,
    prefilled: orderPre.prefilled,
    fixed: orderPre.fixed,
    hiddenCards: orderContainers.cards,
    hiddenScopes: orderContainers.scopes,
    required: ruleRequiredKeys(rules, orderPre.values),
    optional: ruleOptionalKeys(rules, orderPre.values),
    lockedByRule: ruleLockedKeys(rules, orderPre.values),
    unlockedByRule: ruleUnlockedKeys(rules, orderPre.values),
  };

  // ---- container (section / card) visibility ----
  // Sections and cards are order-level structure, so their show/hide targets
  // resolve against the order context. A hidden container then hides every
  // field inside it — which also waives those fields' requiredness, because
  // nothing hidden is ever validated.
  const hiddenSections = new Set<string>();
  const hiddenCards = order.hiddenCards;
  for (const s of sections) {
    if (!containerVisible(sectionTarget(s.id), s.hidden, order.hidden, showTargeted)) hiddenSections.add(s.id);
  }
  /**
   * Widget-node scopes suppressed for the whole form: those inside a hidden
   * widget-type section, and those a hidden ROOT container takes with it. A
   * nested container is resolved per parent instance instead, in the walk.
   */
  const hiddenScopes = new Set<string>();
  for (const s of sections) {
    const w = widgetForSection(def.widgets, s);
    if (!w?.tree) continue;
    const sectionGone = hiddenSections.has(s.id) && (s.type === 'widget' || s.type === 'locations');
    if (sectionGone || order.hiddenScopes.has(w.tree.scope)) {
      flattenWidgetTree(w.tree).forEach((n) => hiddenScopes.add(n.scope));
    }
  }
  /**
   * The cards a field sits inside, innermost first, as `${sectionId}:${cardId}`.
   * Cards nest, so hiding a card hides the cards within it — a field answers
   * to its whole chain, not just the card that directly groups it.
   */
  // evaluateForm runs on every keystroke, so the chains are resolved once per
  // container rather than once per field.
  const cardsByContainer = new Map<string, FormCard[]>();
  for (const s of def.layout?.sections || []) {
    for (const c of s.cards || []) {
      const key = `${s.id}:${c.scope || 'order'}`;
      const list = cardsByContainer.get(key) || [];
      list.push(c);
      cardsByContainer.set(key, list);
    }
  }
  const chains = new Map<string, string[]>();
  const cardChain = (f: FieldDefinition): string[] => {
    const group = (f.ui_meta?.group || '').trim();
    if (!group) return [];
    const key = `${f.section}:${f.scope}:${group}`;
    let chain = chains.get(key);
    if (!chain) {
      const scoped = cardsByContainer.get(`${f.section}:${f.scope}`) || [];
      const ids = chainOf(scoped, group);
      chain = (ids.length ? ids : [group]).map((id) => `${f.section}:${id}`);
      chains.set(key, chain);
    }
    return chain;
  };

  /**
   * Fields of a repeating scope that live in a card, keyed by EVERY card in
   * their chain. A hidden card takes its own fields with it, and inside a
   * widget that question is asked once per instance rather than once per form.
   */
  const cardFieldsByScope: Record<string, Record<string, string[]>> = {};
  for (const f of def.fields) {
    if (f.scope === 'order') continue;
    for (const key of cardChain(f)) ((cardFieldsByScope[f.scope] ||= {})[key] ||= []).push(f.key);
  }

  /**
   * A require/optional target naming a whole SECTION, CARD or WIDGET means
   * every question inside it, on exactly the reading hide already uses — so
   * "make required: Card: Billing contact" demands the questions in the card
   * rather than a key no field answers to. Keys from other scopes are inert:
   * each context is only ever asked about its own fields.
   */
  const fieldsInContainer = (target: string): string[] => {
    if (target.startsWith('sec:')) {
      const id = target.slice(4);
      return def.fields.filter((f) => f.section === id).map((f) => f.key);
    }
    if (target.startsWith('card:')) {
      const key = target.slice(5);
      return def.fields.filter((f) => cardChain(f).includes(key)).map((f) => f.key);
    }
    const scope = target.slice(6);
    return def.fields.filter((f) => f.scope === scope).map((f) => f.key);
  };
  const expandRequiredness = (ctx: CtxEval) => {
    for (const set of [ctx.required, ctx.optional, ctx.lockedByRule, ctx.unlockedByRule]) {
      for (const target of [...set]) {
        if (!isContainerTarget(target)) continue;
        set.delete(target);
        fieldsInContainer(target).forEach((k) => set.add(k));
      }
    }
  };
  expandRequiredness(order);

  /** field keys hidden through their container, split by scope kind */
  const containerHiddenByScope: Record<string, string[]> = {};
  for (const f of def.fields) {
    const inHidden = f.scope === 'order'
      ? hiddenSections.has(f.section) || cardChain(f).some((key) => hiddenCards.has(key))
      : hiddenScopes.has(f.scope);
    if (inHidden) (containerHiddenByScope[f.scope] ||= []).push(f.key);
  }
  (containerHiddenByScope['order'] || []).forEach((k) => order.hidden.add(k));

  // ---- instance contexts (recursive walk) ----
  const byInstance = new Map<string, CtxEval>();
  /**
   * Every context a scored question could be asked in, tagged with the scope
   * whose fields it answers for. The order scope is asked once; a question
   * inside a repeatable widget is asked once per instance and scores once per
   * instance, which is how a per-location scorecard totals up.
   */
  const scoringCtxs: { scope: string; ctx: CtxEval }[] = [{ scope: 'order', ctx: order }];
  const instanceCountsById = new Map<string, Record<string, number>>();
  /** effective own-values of every instance, per scope — feeds `sum` calcs */
  const valuesByScope: Record<string, Record<string, unknown>[]> = {};

  const nodes = nodesByScope(def);
  /**
   * `suppressed` = the container holding this instance is hidden, so nothing
   * in it is on the form. It travels down the whole subtree: hiding a location
   * takes its devices, and the lines on those devices, with it.
   *
   * Returns what this instance adds to every conditional count of the level
   * above it: one for each of its own container's counts whose condition it
   * meets, plus everything nested inside it. Those are tallied on the way
   * back UP because a condition reads an instance's settled answers, which
   * only exist once its context has been built — so, unlike the automatic
   * counts, they are not among the values this form's own rules can read.
   * They ride the submission; the server derives the same numbers from the
   * stored answers (cleanSubmissionData in server/src/routes/public.ts).
   */
  const walk = (
    inst: WidgetInstance, scope: string, ancestors: Record<string, unknown>, suppressed: boolean
  ): Record<string, number> => {
    // Automatic per-instance counts (this location's devices, this device's
    // lines, this location's lines across every device…) are available to
    // rules and calcs exactly like answered fields, and ride along to the
    // submission payload.
    const counts = nodes[scope] ? instanceCounts(inst, nodes[scope]) : {};
    const raw = { ...ancestors, ...counts, ...inst.values };
    // applyPrefill looks up touched by plain key; instance touches are stored
    // namespaced by instance id, so present them under their plain keys here.
    const touched: Record<string, boolean> = {};
    for (const k of keysByScope[scope] || []) {
      if (state.touched[valueKey(inst.id, k)]) touched[k] = true;
    }
    const pre = applyPrefill(rules, raw, touched, kinds);
    const hidden = ruleHiddenKeys(rules, pre.values, cfg);
    const containers = hiddenContainersOf(hidden, showTargeted, hiddenDefaults);
    const ctx: CtxEval = {
      values: pre.values,
      hidden,
      locked: pre.locked,
      prefilled: pre.prefilled,
      fixed: pre.fixed,
      hiddenCards: containers.cards,
      hiddenScopes: containers.scopes,
      required: ruleRequiredKeys(rules, pre.values),
      optional: ruleOptionalKeys(rules, pre.values),
      lockedByRule: ruleLockedKeys(rules, pre.values),
      unlockedByRule: ruleUnlockedKeys(rules, pre.values),
    };
    expandRequiredness(ctx);
    (containerHiddenByScope[scope] || []).forEach((k) => ctx.hidden.add(k));
    // Nothing inside a hidden container is asked, which is also what waives
    // its requiredness — the renderer and the validator read the same set.
    if (suppressed) (keysByScope[scope] || []).forEach((k) => ctx.hidden.add(k));
    // A card this instance hides takes the answers inside it with it, which is
    // also what waives their requiredness — nothing hidden is ever validated.
    for (const card of ctx.hiddenCards) {
      (cardFieldsByScope[scope]?.[card] || []).forEach((k) => ctx.hidden.add(k));
    }
    byInstance.set(inst.id, ctx);
    scoringCtxs.push({ scope, ctx });
    instanceCountsById.set(inst.id, counts);
    const own: Record<string, unknown> = { ...counts };
    for (const k of keysByScope[scope] || []) own[k] = pre.values[k];
    (valuesByScope[scope] ||= []).push(own);
    // Every level reports every count beneath it, a 0 included.
    const held: Record<string, number> = {};
    for (const child of nodes[scope]?.children || []) {
      for (const n of flattenWidgetTree(child)) (n.counts || []).forEach((c) => { held[c.key] = 0; });
    }
    for (const [childScope, childList] of Object.entries(inst.children)) {
      const gone = suppressed || ctx.hiddenScopes.has(childScope) || hiddenScopes.has(childScope);
      childList.forEach((c) => tally(held, walk(c, childScope, pre.values, gone)));
    }
    Object.assign(counts, held);
    Object.assign(own, held);
    const up = { ...held };
    for (const c of nodes[scope]?.counts || []) {
      tally(up, { [c.key]: groupHolds(c.match, c.when, ctx.values) ? 1 : 0 });
    }
    return up;
  };
  const conditionalCounts: Record<string, number> = {};
  for (const n of Object.values(nodes)) (n.counts || []).forEach((c) => { conditionalCounts[c.key] = 0; });
  for (const [scope, list] of Object.entries(state.widgets)) {
    list.forEach((inst) => tally(conditionalCounts, walk(inst, scope, order.values, hiddenScopes.has(scope))));
  }

  // ---- calcs: counts/math/join on the order context; sum across instances ----
  const sumOver = (field: string, ofScope: string) =>
    (valuesByScope[ofScope] || []).reduce((acc, vals) => acc + num(vals[field]), 0);

  /**
   * Scorecard totals: the points on the ANSWER OPTIONS, added up over the
   * questions that are actually on the form right now.
   *
   * `renderableIn` is the authority on that last part, the same function the
   * renderer asks — so a question a rule is hiding is outside the totals for
   * exactly as long as nobody is being asked it, and a scorecard cannot
   * demand points for a section it never showed. Narrowing to one `section`
   * is what gives a scorecard its per-area subtotals.
   */
  const scored = def.fields.filter((f) => isScored(f));
  const scoreOver = (section?: string): ScoreTotals => {
    let total = NO_SCORE;
    for (const { scope, ctx } of scoringCtxs) {
      for (const f of scored) {
        if (f.scope !== scope) continue;
        if (section && f.section !== section) continue;
        if (!renderableIn(f, ctx, cfg, { ruleTargeted, showTargeted })) continue;
        const got = scoreAnswer(f, ctx.values[f.key]);
        if (got) total = addTotals(total, got);
      }
    }
    return total;
  };
  const calcs = computeCalcs(rules, order.values, counts, sumOver, scoreOver);

  return {
    order, byInstance, instanceCounts: instanceCountsById, calcs, counts, conditionalCounts,
    ruleTargeted, showTargeted, hiddenSections, hiddenCards,
  };
}

/**
 * Should a field render in the given context? Flag visibility, rule visibility,
 * and (for fields no rule targets) the legacy ui_meta.showWhen — evaluated
 * identically in the builder's test mode and on the public form.
 *
 * A builder-hidden field (the '__hidden' sentinel) that a SHOW action targets
 * is rule-driven: it renders exactly while the rule fires. Without such a rule
 * it never renders.
 */
export function renderableIn(
  f: FieldDefinition, ctx: CtxEval, cfg: FormConfig,
  ev: Pick<FormEval, 'ruleTargeted' | 'showTargeted'>
): boolean {
  const builderHidden = f.ui_meta?.showFlag === '__hidden';
  const visible = builderHidden ? ev.showTargeted.has(f.key) : fieldVisible(f, cfg);
  return visible
    && !ctx.hidden.has(f.key)
    && (ev.ruleTargeted.has(f.key) || showWhenPasses(f, ctx.values, cfg));
}
